/**
 * Dashboard Widget Types and Configuration
 */

export enum DashboardWidgetId {
  // KPI Widgets - catalog definitions; dashboards show KPIs enabled per tenant and role
  /** Unique currently enrolled students. API key stays `total-students`. */
  TotalStudents = "total-students",
  TotalClasses = "total-classes",
  ActiveEnrollments = "active-enrollments",
  TotalTeachers = "total-teachers",
  TotalSeasons = "total-seasons",
  TotalLocations = "total-locations",
  TotalClassrooms = "total-classrooms",
  TotalPolicies = "total-policies",

  // Regular Widgets (Displayed in 2-column grid, last one full width if odd)
  Revenue = "revenue",
  RevenueHistory = "revenue-history",
  TopClasses = "top-classes",
  EnrollmentStatus = "enrollment-status",
  /** Retired: use Daily workflow on the dashboard. */
  RecentPendingEnrollments = "recent-pending-enrollments",
  /** Retired: use Daily workflow on the dashboard. */
  ScheduledDropEnrollments = "scheduled-drop-enrollments",
  /** Retired: 4-row list. Kept so saved API settings can be migrated. */
  NewEnrollments = "new-enrollments",
  UpcomingClasses = "upcoming-classes",
  LowEnrollmentClasses = "low-enrollment-classes",
  TeacherLoad = "teacher-load",
  LocationUsage = "location-usage",
  ClassroomUtilization = "classroom-utilization",
  ActiveSeasons = "active-seasons",
  Locations = "locations",
  Classrooms = "classrooms",
  Policies = "policies",
  /** Retired: use Daily workflow on the dashboard. */
  Waitlist = "waitlist",
  Attendance = "attendance",
  StudentsProgress = "students-progress",
  Payments = "payments",
  ClassesCalendar = "classes-calendar",
  EnrollmentsCalendar = "enrollments-calendar",
  RecentActivity = "recent-activity",
  PrivateLessonsCalendar = "private-lessons-calendar",
  ToDo = "to-do",
}

export type UserRole = "Admin" | "Teacher" | "Staff";

export interface DashboardWidgetConfig {
  id: DashboardWidgetId;
  label: string;
  category: "kpi" | "regular";
  description?: string;
}

export const ALL_WIDGETS: DashboardWidgetConfig[] = [
  // KPI Widgets
  {
    id: DashboardWidgetId.TotalStudents,
    label: "Enrolled Students",
    category: "kpi",
    description: "Unique students currently enrolled in at least one class",
  },
  {
    id: DashboardWidgetId.TotalClasses,
    label: "Total Classes",
    category: "kpi",
    description: "Total number of classes",
  },
  {
    id: DashboardWidgetId.ActiveEnrollments,
    label: "Active Enrollments",
    category: "kpi",
    description: "Total active enrollments",
  },
  {
    id: DashboardWidgetId.TotalTeachers,
    label: "Total Teachers",
    category: "kpi",
    description: "Total number of teachers",
  },
  {
    id: DashboardWidgetId.TotalSeasons,
    label: "Seasons",
    category: "kpi",
    description: "Total number of seasons",
  },
  {
    id: DashboardWidgetId.TotalLocations,
    label: "Locations",
    category: "kpi",
    description: "Total number of locations",
  },
  {
    id: DashboardWidgetId.TotalClassrooms,
    label: "Classrooms",
    category: "kpi",
    description: "Total number of classrooms",
  },
  {
    id: DashboardWidgetId.TotalPolicies,
    label: "Policies",
    category: "kpi",
    description: "Total number of policies",
  },
  // Regular Widgets
  {
    id: DashboardWidgetId.Revenue,
    label: "Revenue",
    category: "regular",
    description: "Revenue overview and recent billing transactions",
  },
  {
    id: DashboardWidgetId.TopClasses,
    label: "Top Classes",
    category: "regular",
    description: "Most popular classes by enrollment",
  },
  {
    id: DashboardWidgetId.EnrollmentStatus,
    label: "Enrollment Status",
    category: "regular",
    description: "Enrollment counts by status (enrolled, pending, waitlist).",
  },
  {
    id: DashboardWidgetId.UpcomingClasses,
    label: "Today's classes",
    category: "regular",
    description: "Classes still happening or starting later today",
  },
  {
    id: DashboardWidgetId.LowEnrollmentClasses,
    label: "Low enrollment classes",
    category: "regular",
    description: "Classes with five or fewer enrolled students",
  },
  {
    id: DashboardWidgetId.TeacherLoad,
    label: "Teacher Load",
    category: "regular",
    description: "Weekly class sessions assigned per teacher",
  },
  {
    id: DashboardWidgetId.LocationUsage,
    label: "Location Usage",
    category: "regular",
    description: "Class distribution across locations",
  },
  {
    id: DashboardWidgetId.ClassroomUtilization,
    label: "Classroom Utilization",
    category: "regular",
    description: "Classroom usage statistics",
  },
  {
    id: DashboardWidgetId.ActiveSeasons,
    label: "Active Seasons",
    category: "regular",
    description: "Current and upcoming seasons",
  },
  {
    id: DashboardWidgetId.Locations,
    label: "Locations",
    category: "regular",
    description: "All locations with details",
  },
  {
    id: DashboardWidgetId.Classrooms,
    label: "Classrooms",
    category: "regular",
    description: "All classrooms with usage",
  },
  {
    id: DashboardWidgetId.Policies,
    label: "Policies",
    category: "regular",
    description: "All policies and waivers",
  },
  {
    id: DashboardWidgetId.Attendance,
    label: "Attendance",
    category: "regular",
    description: "Attendance statistics",
  },
  {
    id: DashboardWidgetId.StudentsProgress,
    label: "Students Progress",
    category: "regular",
    description: "Student enrollment progress",
  },
  {
    id: DashboardWidgetId.Payments,
    label: "Payments",
    category: "regular",
    description: "Payment history and balance",
  },
  {
    id: DashboardWidgetId.ClassesCalendar,
    label: "Classes Calendar",
    category: "regular",
    description: "View all classes by date",
  },
  {
    id: DashboardWidgetId.EnrollmentsCalendar,
    label: "Enrollments Calendar",
    category: "regular",
    description: "View enrollments by enrollment date",
  },
  {
    id: DashboardWidgetId.RecentActivity,
    label: "Enrollment changes",
    category: "regular",
    description: "Enrollment status changes from the last 7 days (activity log).",
  },
  {
    id: DashboardWidgetId.PrivateLessonsCalendar,
    label: "Private Lesson Calendar",
    category: "regular",
    description: "View scheduled private lessons. Click a day to see details.",
  },
  {
    id: DashboardWidgetId.ToDo,
    label: "My Tasks",
    category: "regular",
    description: "Open tasks assigned to you",
  },
];

/**
 * Staff settings UI: hide financial / company P&L style widgets (no Balance route for staff).
 * Everything else matches the admin catalog so front desk can configure what matches their access
 * (classes, enrollments, policies, etc.). Staff dashboard uses the same widget data as admin
 * via `useStudioDashboardWidgetData` (excluding Revenue/Payments widgets).
 */
export const DASHBOARD_WIDGET_IDS_EXCLUDED_FOR_STAFF: readonly DashboardWidgetId[] =
  [DashboardWidgetId.Revenue, DashboardWidgetId.Payments] as const;

const excludedForStaffSet = new Set<DashboardWidgetId>(
  DASHBOARD_WIDGET_IDS_EXCLUDED_FOR_STAFF,
);

/**
 * Restricts which widget ids appear per role when editing dashboard settings UI.
 * The actual enabled state lives in `/api/widgets/settings` (roles array per widget key).
 *
 * – Admin: full catalog.
 * – Teacher: Teaching-focused widgets (assigned-class scope).
 * – Staff: Full catalog minus `DASHBOARD_WIDGET_IDS_EXCLUDED_FOR_STAFF`.
 */
/** KPI + regular widgets admins may assign to the teacher dashboard (teaching scope only). */
export const DASHBOARD_WIDGET_IDS_OFFERED_BY_ROLE: Record<
  UserRole,
  readonly DashboardWidgetId[]
> = {
  Admin: ALL_WIDGETS.map((w) => w.id),
  Teacher: [
    DashboardWidgetId.TotalStudents,
    DashboardWidgetId.TotalClasses,
    DashboardWidgetId.TotalLocations,
    DashboardWidgetId.ToDo,
    DashboardWidgetId.UpcomingClasses,
    DashboardWidgetId.LowEnrollmentClasses,
    DashboardWidgetId.PrivateLessonsCalendar,
    DashboardWidgetId.TopClasses,
    DashboardWidgetId.RecentActivity,
    DashboardWidgetId.Locations,
    DashboardWidgetId.ClassesCalendar,
  ],
  Staff: ALL_WIDGETS.map((w) => w.id).filter(
    (id) => !excludedForStaffSet.has(id),
  ),
} as const;

const offeredByRoleSet: Record<UserRole, ReadonlySet<DashboardWidgetId>> = {
  Admin: new Set(DASHBOARD_WIDGET_IDS_OFFERED_BY_ROLE.Admin),
  Teacher: new Set(DASHBOARD_WIDGET_IDS_OFFERED_BY_ROLE.Teacher),
  Staff: new Set(DASHBOARD_WIDGET_IDS_OFFERED_BY_ROLE.Staff),
};

/** Config entries for a role’s offered widgets (same order as ALL_WIDGETS). */
export function getDashboardWidgetsOfferedForRole(
  role: UserRole,
): DashboardWidgetConfig[] {
  const allowed = offeredByRoleSet[role];
  return ALL_WIDGETS.filter((w) => allowed.has(w.id));
}

export interface DashboardWidgetSettings {
  [role: string]: {
    kpiWidgets: DashboardWidgetId[]; // KPI tiles from catalog keys enabled for this layout
    regularWidgets: DashboardWidgetId[]; // Revenue is full width; others pack in twos; last unpaired is full width
  };
}

// Default widget configurations per role
export const DEFAULT_DASHBOARD_SETTINGS: DashboardWidgetSettings = {
  Admin: {
    kpiWidgets: [
      DashboardWidgetId.TotalStudents,
      DashboardWidgetId.TotalClasses,
      DashboardWidgetId.ActiveEnrollments,
      DashboardWidgetId.TotalTeachers,
    ],
    regularWidgets: [
      DashboardWidgetId.ClassesCalendar,
      DashboardWidgetId.Revenue,
      DashboardWidgetId.EnrollmentsCalendar,
      DashboardWidgetId.TopClasses,
      DashboardWidgetId.RecentActivity,
      DashboardWidgetId.EnrollmentStatus,
      DashboardWidgetId.UpcomingClasses,
      DashboardWidgetId.LowEnrollmentClasses,
      DashboardWidgetId.TeacherLoad,
    ],
  },
  Teacher: {
    kpiWidgets: [
      DashboardWidgetId.TotalClasses,
      DashboardWidgetId.TotalStudents,
    ],
    regularWidgets: [
      DashboardWidgetId.ToDo,
      DashboardWidgetId.UpcomingClasses,
      DashboardWidgetId.PrivateLessonsCalendar,
    ],
  },
  Staff: {
    kpiWidgets: [
      DashboardWidgetId.ActiveEnrollments,
      DashboardWidgetId.TotalStudents,
      DashboardWidgetId.TotalClasses,
      DashboardWidgetId.TotalTeachers,
    ],
    regularWidgets: [
      DashboardWidgetId.PrivateLessonsCalendar,
      DashboardWidgetId.RecentActivity,
      DashboardWidgetId.EnrollmentStatus,
    ],
  },
};

/** Removed from the catalog; saved API rows are dropped or remapped at layout time. */
export const RETIRED_DASHBOARD_WIDGET_IDS = new Set<DashboardWidgetId>([
  DashboardWidgetId.NewEnrollments,
  DashboardWidgetId.RecentPendingEnrollments,
  DashboardWidgetId.ScheduledDropEnrollments,
  DashboardWidgetId.Waitlist,
]);

export function getDashboardWidgetLabel(widgetId: DashboardWidgetId): string {
  return (
    ALL_WIDGETS.find((widget) => widget.id === widgetId)?.label ?? widgetId
  );
}
